/**
 * Jobs List Component for Compute Jobs Page
 */

import React, { useContext, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import './jobs-list.css';

// components
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import PleaseWait from '../../tools/please-wait';
import MiniButton from '../../tools/mini-button';
import StatusIndicator from '../../tools/status-indicator';

// types
import { ComputeJob } from '../compute-jobs/types';
import { Event, EventType, EventTarget } from '../../utils/events';

// icons
import { Info } from 'lucide-react';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { fnD_getComputeJobs } from '../../app/data-actions';

import { setDisplayedJob } from './compute-jobs-slice';

interface JobsListProps {
  setEventHandler: (handler: any) => void;
}

/**
 * Get status badge class based on job status
 */
const getStatusBadgeClass = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'running') return 'status-running';
  if (statusLower === 'completed') return 'status-completed';
  if (statusLower === 'failed' || statusLower === 'error') return 'status-failed';
  if (statusLower === 'pending') return 'status-pending';
  return 'status-unknown';
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

export default function JobsList(props: JobsListProps) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state variables.
  const sD_computeJobs: ComputeJob[] = useAppSelector((state) => state.data.computeJobs);
  const sD_computeJobsLoading: boolean = useAppSelector(
    (state) => state.data.computeJobsLoading,
  );

  // local state variables.
  const sDisplayedJob: ComputeJob | undefined = useAppSelector(
    (state) => state.computeJobs.displayedJob,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  const eventHandler = (args: { event: Event }) => {
    console.log('JobsList eventHandler:', args.event);
  };

  const handleRowClick = (jobId: string) => {
    setSelectedJobId(jobId === selectedJobId ? undefined : jobId);
  };

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  useEffect(() => {
    props.setEventHandler(eventHandler);
  }, []);

  //  -------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  -------------------------------------------------

  if (error) {
    return (
      <div className="jobs-list-error">
        <p>{error}</p>
        <ToolButton
          name="retry-button"
          type={ToolButtonType.PRIMARY}
          onClick={() => dispatch(fnD_getComputeJobs())}
          tooltip="Retry"
          text="Retry"
        />
      </div>
    );
  }

  return (
    <div className="jobs-list-container">
      <PleaseWait text="Loading jobs....." visible={sD_computeJobsLoading} />

      {/*<div className="jobs-list-table-wrapper">*/}
      <table className="jobs-list-table">
        <tbody>
          {sD_computeJobs.map((job) => (
            <tr
              key={job.job_id}
              className={selectedJobId === job.job_id ? 'selected' : ''}
              onClick={() => handleRowClick(job.job_id)}
            >
              <td
                style={{
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  width: '50px',
                }}
              >
                <MiniButton
                  className="text-colour"
                  data-mini-button="T"
                  data-selected={job.job_id === sDisplayedJob?.job_id ? 'T' : 'F'}
                  title="View information"
                  onClick={() => dispatch(setDisplayedJob(job))}
                >
                  <Info width={30} height={30} strokeWidth={1.5} />
                </MiniButton>
              </td>
              <td
                className="job-name-cell"
                style={{
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
                title={job.job_name}
              >
                {job.job_name}
              </td>
              <td
                className="timestamp-cell"
                style={{
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {formatTimestamp(job.submitted_at)}
              </td>
              <td className="status-cell">
                <StatusIndicator status={job.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/*</div>*/}
    </div>
  );
}
